import { useState } from 'react';
import { RangeSlider } from './filters/RangeSlider';
import { FilterPresets } from './filters/FilterPresets';
import { isEnabled } from '../../lib/featureFlags';

/**
 * Advanced filter panel with presets and range sliders
 *
 * @param {Object} props
 * @param {Object} props.filters - Current filter state
 * @param {Function} props.updateFilters - Update filter callback
 * @param {Function} props.resetFilters - Reset all filters callback
 */
export function AdvancedFilterPanel({ filters, updateFilters, resetFilters }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const useAdvancedFilters = isEnabled('ADVANCED_FILTERS');

  if (!useAdvancedFilters) {
    return null; // Hide if feature flag is disabled
  }

  const handlePresetSelect = (presetId, presetFilters) => {
    // Toggle preset: if already active, reset; otherwise apply
    if (filters.activePreset === presetId) {
      resetFilters();
    } else {
      updateFilters({
        ...presetFilters,
        activePreset: presetId
      });
    }
  };

  const activeFilterCount = [
    filters.confidenceRange?.[0] > 0 || filters.confidenceRange?.[1] < 100,
    filters.marginRange?.[0] > -20 || filters.marginRange?.[1] < 20,
    filters.competitivenessRange?.[0] > 0 || filters.competitivenessRange?.[1] < 100,
    filters.openSeatsOnly,
    filters.activePreset
  ].filter(Boolean).length;

  return (
    <div className="bg-slate-800 rounded-lg p-4 space-y-4">
      {/* Presets (always visible) */}
      <FilterPresets
        activePreset={filters.activePreset}
        onSelect={handlePresetSelect}
      />

      {/* Advanced toggle */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-sm font-medium text-slate-300 hover:text-slate-100 transition"
      >
        <span>⚙️ Advanced Filters</span>
        <span className="text-slate-500">{isExpanded ? '▲' : '▼'}</span>
      </button>

      {/* Advanced filters (collapsible) */}
      {isExpanded && (
        <div className="space-y-6 pt-2">
          <RangeSlider
            label="Confidence Range"
            min={0}
            max={100}
            value={filters.confidenceRange || [0, 100]}
            onChange={(val) => updateFilters({ confidenceRange: val })}
            formatLabel={(v) => `${v}%`}
          />

          <RangeSlider
            label="Margin Range"
            min={-20}
            max={20}
            value={filters.marginRange || [-20, 20]}
            onChange={(val) => updateFilters({ marginRange: val })}
            formatLabel={(v) => v > 0 ? `D+${v}` : v < 0 ? `R+${Math.abs(v)}` : 'Even'}
            step={0.5}
          />

          <RangeSlider
            label="Competitiveness Score"
            min={0}
            max={100}
            value={filters.competitivenessRange || [0, 100]}
            onChange={(val) => updateFilters({ competitivenessRange: val })}
            formatLabel={(v) => `${v}/100`}
          />

          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={filters.openSeatsOnly || false}
              onChange={(e) => updateFilters({ openSeatsOnly: e.target.checked })}
              className="rounded border-slate-600 bg-slate-700 text-accent-blue focus:ring-accent-blue"
            />
            Open Seats Only
          </label>
        </div>
      )}

      {/* Active filters bar */}
      {activeFilterCount > 0 && (
        <div className="flex items-center justify-between pt-3 border-t border-slate-700">
          <span className="text-xs text-slate-400">
            {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} applied
          </span>
          <button
            onClick={resetFilters}
            className="flex items-center gap-1 text-xs text-accent-blue hover:text-blue-300 transition"
          >
            <span>✕</span>
            Clear All
          </button>
        </div>
      )}
    </div>
  );
}
